import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import alertaService from '../../services/Alerta/AlertaService';
import { registrarLectura } from './lecturaAlerta';

/** Cada cuanto se consulta el contador. */
const INTERVALO_MS = 30000;
/** Cuantas alertas muestra el desplegable. Para ver el resto esta la pantalla. */
const MAX_EN_PANEL = 15;

/**
 * Campanita de alertas en el topbar.
 *
 * Consulta el contador cada 30 segundos. No es un canal de push y no hace falta
 * que lo sea: entre que nace una alerta y se notifica pasan hoy 73 segundos de
 * mediana -- el motor de alarmas y el envio corren cada uno por minuto -- asi
 * que sondear cada 30 no agrega demora perceptible sobre ese piso.
 */
const AlertaCampanita = () => {

    const navigate = useNavigate();
    const panel = useRef(null);
    const timer = useRef(null);

    const [noLeidas, setNoLeidas] = useState(0);
    const [porRevisar, setPorRevisar] = useState(0);
    const [alertas, setAlertas] = useState([]);
    const [cargando, setCargando] = useState(false);

    /** Sin sesion no se consulta nada. */
    const haySesion = () => {
        const jwt = localStorage.getItem('jwt');
        return !!jwt && jwt.replaceAll('"', '').length > 0;
    };

    const leerContador = useCallback(async () => {
        if (!haySesion()) return;
        // Con la pestania en segundo plano no tiene sentido consultar: el usuario
        // no lo esta mirando y se sigue golpeando la base cada 30 segundos.
        if (typeof document !== 'undefined' && document.hidden) return;
        try {
            const data = await alertaService.contador();
            setNoLeidas(data?.noLeidas ?? 0);
            setPorRevisar(data?.porRevisar ?? 0);
        } catch (e) {
            // Un error de red no puede romper el topbar ni llenar la consola en
            // cada ciclo: se deja el ultimo valor conocido y se reintenta solo.
        }
    }, []);

    useEffect(() => {
        leerContador();
        timer.current = setInterval(leerContador, INTERVALO_MS);
        // Al volver a la pestania se refresca de inmediato, sin esperar el ciclo.
        const alVolver = () => { if (!document.hidden) leerContador(); };
        document.addEventListener('visibilitychange', alVolver);
        return () => {
            if (timer.current) clearInterval(timer.current);
            document.removeEventListener('visibilitychange', alVolver);
        };
    }, [leerContador]);

    const abrir = async (e) => {
        panel.current.toggle(e);
        setCargando(true);
        try {
            const data = await alertaService.misAlertas(true, MAX_EN_PANEL);
            setAlertas(Array.isArray(data) ? data : []);
        } catch (err) {
            setAlertas([]);
        } finally {
            setCargando(false);
        }
    };

    const irADetalle = async (a) => {
        // Registra la apertura (leida + auditoria) en segundo plano: no se hace
        // esperar al usuario por el GPS. Nunca falla: si el endpoint nuevo no
        // responde, cae a marcarLeida con el id del aviso.
        registrarLectura([a.monitoreoDetId], 'CAMPANITA', { avisoId: a.id });
        setAlertas((prev) => prev.filter((x) => x.id !== a.id));
        setNoLeidas((n) => Math.max(0, n - 1));
        panel.current.hide();
        navigate('/mis-alertas');
    };

    const leerTodas = async () => {
        try {
            await alertaService.marcarTodasLeidas();
            setAlertas([]);
            setNoLeidas(0);
        } catch (err) {
            // Si falla, el proximo ciclo del contador devuelve el valor real.
            leerContador();
        }
    };

    const fechaCorta = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('es-EC', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <>
            <button
                type="button"
                className="mr-3 layout-topbar-action rounded-circle p-link p-overlay-badge"
                onClick={abrir}
                title={porRevisar > 0
                    ? `${noLeidas} sin leer · ${porRevisar} por revisar`
                    : `${noLeidas} sin leer`}
                aria-label={`Alertas: ${noLeidas} sin leer`}>
                <i className="pi pi-bell fs-large"></i>
                {noLeidas > 0 && (
                    <Badge value={noLeidas > 99 ? '99+' : noLeidas}
                           severity={porRevisar > 0 ? 'danger' : 'info'} />
                )}
            </button>

            <OverlayPanel ref={panel} style={{ width: '26rem', maxWidth: '95vw' }}>
                <div className="flex justify-content-between align-items-center mb-2">
                    <span className="font-bold">Alertas sin leer</span>
                    {noLeidas > 0 && (
                        <Button label="Marcar todas" className="p-button-text p-button-sm"
                                onClick={leerTodas} />
                    )}
                </div>

                {porRevisar > 0 && (
                    <div className="mb-2 p-2 border-round surface-100 text-sm">
                        <i className="pi pi-exclamation-circle mr-2"></i>
                        Tenés <b>{porRevisar}</b> {porRevisar === 1 ? 'alerta' : 'alertas'} por marcar como revisada.
                    </div>
                )}

                {cargando && <div className="text-center p-3 text-sm">Cargando…</div>}

                {!cargando && alertas.length === 0 && (
                    <div className="text-center p-3 text-sm text-color-secondary">
                        No hay alertas sin leer.
                    </div>
                )}

                {!cargando && alertas.length > 0 && (
                    <ul className="list-none p-0 m-0" style={{ maxHeight: '22rem', overflowY: 'auto' }}>
                        {alertas.map((a) => (
                            <li key={a.id}
                                className="p-2 border-bottom-1 surface-border cursor-pointer hover:surface-100"
                                onClick={() => irADetalle(a)}>
                                <div className="flex justify-content-between align-items-start">
                                    <span className="font-medium text-sm">
                                        {a.tipoEvento} · {a.evento}
                                    </span>
                                    <small className="text-color-secondary white-space-nowrap ml-2">
                                        {fechaCorta(a.fechaEvento)}
                                    </small>
                                </div>
                                <div className="text-sm text-color-secondary">
                                    {a.dispositivo || '—'}
                                    {a.booking ? ` · ${a.booking}` : ''}
                                </div>
                                {a.requiereConfirmacion && !a.revisada && (
                                    <small className="text-orange-600">Requiere marcar “Revisado”</small>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="text-center mt-2">
                    <Button label="Ver todas" className="p-button-text p-button-sm"
                            onClick={() => { panel.current.hide(); navigate('/mis-alertas'); }} />
                </div>
            </OverlayPanel>
        </>
    );
};

export default AlertaCampanita;
